import React, { useState, useEffect, useCallback } from 'react';
import { 
  UserCheck, 
  Calendar, 
  Search, 
  CheckCircle, 
  XCircle, 
  Clock,
  Loader2,
  Eye,
  ToggleLeft,
  ToggleRight,
  Download,
  Filter,
  CheckSquare,
  Square,
  UserPlus,
  Settings,
  RefreshCw,
  Cpu
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import { getBatches, getBatchEnrollments, COLLECTIONS } from '../../firebase/collections';
import { collection, query, where, orderBy, onSnapshot, getDocs, getDoc, setDoc, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const AttendanceManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [allowStudentMarking, setAllowStudentMarking] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, present, absent, late
  const [showFaceModal, setShowFaceModal] = useState(null);
  const [presentStudents, setPresentStudents] = useState(new Set());
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [isFetchingBiometric, setIsFetchingBiometric] = useState(false);
  const [biometricSettings, setBiometricSettings] = useState({
    apiUrl: '',
    apiKey: '',
    mappingField: 'email', // 'email', 'userId', 'biometricId'
    responsePath: '', // e.g., 'data' or 'records'
    studentIdField: 'student_id', // Field in API response
    statusField: 'status', // Field in API response
    presentValue: 'present' // Value in API response for present
  });

  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadBatches();
    if (coachingId) {
      loadBiometricSettings();
    }
  }, [coachingId]);

  const loadBiometricSettings = async () => {
    if (!coachingId) return;
    try {
      const settingsRef = doc(db, 'coachingSettings', coachingId);
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        if (data.biometricConfig) {
          setBiometricSettings(data.biometricConfig);
        }
      }
    } catch (error) {
      console.error('Error loading biometric settings:', error);
    }
  };

  const saveBiometricSettings = async (newSettings) => {
    if (!coachingId) return;
    try {
      const settingsRef = doc(db, 'coachingSettings', coachingId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          biometricConfig: newSettings,
          updatedAt: serverTimestamp()
        });
      } else {
        await setDoc(settingsRef, {
          biometricConfig: newSettings,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      setBiometricSettings(newSettings);
      setShowBiometricModal(false);
      toast.success('Biometric settings saved successfully');
    } catch (error) {
      console.error('Error saving biometric settings:', error);
      toast.error('Failed to save biometric settings');
    }
  };

  const fetchBiometricAttendance = async () => {
    if (!biometricSettings.apiUrl) {
      toast.error('Please configure biometric API first');
      setShowBiometricModal(true);
      return;
    }

    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    setIsFetchingBiometric(true);
    try {
      // Build the API URL with date filter if possible, otherwise use base URL
      let fetchUrl = biometricSettings.apiUrl;
      if (fetchUrl.includes('?')) {
        fetchUrl += `&date=${selectedDate}`;
      } else {
        fetchUrl += `?date=${selectedDate}`;
      }

      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${biometricSettings.apiKey}`,
          'X-API-KEY': biometricSettings.apiKey // Some APIs use this header
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Extract records from response
      let records = data;
      if (biometricSettings.responsePath) {
        const paths = biometricSettings.responsePath.split('.');
        for (const path of paths) {
          records = records[path];
        }
      }

      if (!Array.isArray(records)) {
        throw new Error('API response is not an array of records');
      }

      // Process records and update present students
      const newPresentSet = new Set(presentStudents);
      let matchCount = 0;

      records.forEach(record => {
        const apiStudentId = record[biometricSettings.studentIdField];
        const apiStatus = record[biometricSettings.statusField];
        
        // Only process if status is present (or status field is missing, assuming all returned records are present)
        if (!apiStatus || apiStatus === biometricSettings.presentValue) {
          // Find matching student in our enrollments
          const matchedStudent = enrollments.find(student => {
            if (biometricSettings.mappingField === 'email') {
              return student.studentEmail === apiStudentId;
            } else if (biometricSettings.mappingField === 'userId') {
              return student.studentId === apiStudentId;
            } else {
              // Fallback to biometricId if it exists in user profile (to be implemented)
              return student.biometricId === apiStudentId;
            }
          });

          if (matchedStudent) {
            newPresentSet.add(matchedStudent.studentId);
            matchCount++;
          }
        }
      });

      setPresentStudents(newPresentSet);
      toast.success(`Biometric sync complete: ${matchCount} students matched for ${selectedDate}`);
    } catch (error) {
      console.error('Error fetching biometric attendance:', error);
      toast.error(`Biometric fetch failed: ${error.message}`);
    } finally {
      setIsFetchingBiometric(false);
    }
  };

  useEffect(() => {
    if (selectedBatch) {
      loadEnrollments();
      checkMarkingPermission();
      // Reset present students when date changes
      setPresentStudents(new Set());
    }
  }, [selectedBatch, selectedDate]);

  useEffect(() => {
    // Initialize present students from existing attendance records for the selected date only
    if (attendanceRecords.length > 0 && enrollments.length > 0 && selectedDate) {
      const presentSet = new Set();
      attendanceRecords.forEach(record => {
        // Only include records that match the selected date
        if (record.status === 'present' && record.date === selectedDate) {
          presentSet.add(record.studentId);
        }
      });
      setPresentStudents(presentSet);
    } else {
      setPresentStudents(new Set());
    }
  }, [attendanceRecords, enrollments, selectedDate]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const result = await getBatches(coachingId || null);
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async () => {
    if (!selectedBatch) return;
    try {
      const result = await getBatchEnrollments(selectedBatch.id);
      if (result.success && result.data) {
        const batchStudents = [];
        let missingUserIdCount = 0;
        
        // Extract emails from enrollments and query users
        for (const enrollment of result.data) {
          let studentId = enrollment.userId;
          let userData = enrollment.userData || {};
          const email = enrollment.email || enrollment.studentEmail || userData.email;

          // If userId is missing, try to find user by email
          if (!studentId && email) {
            try {
              const userQuery = query(
                collection(db, COLLECTIONS.USERS),
                where('email', '==', email)
              );
              const userSnapshot = await getDocs(userQuery);
              
              if (!userSnapshot.empty) {
                const userDoc = userSnapshot.docs[0];
                studentId = userDoc.id;
                userData = { ...userData, ...userDoc.data() };
              }
            } catch (err) {
              console.error('Error querying user by email:', err);
            }
          }

          // Fallback: If still no studentId, use enrollment ID or email to ensure they appear
          if (!studentId) {
            studentId = email || enrollment.id;
            missingUserIdCount++;
          }
          
          batchStudents.push({
            ...enrollment,
            studentId,
            studentName: userData.fullName || userData.name || email || 'Unknown',
            studentEmail: email || ''
          });
        }
        
        setEnrollments(batchStudents);
        console.log('✅ Loaded enrollments:', {
          total: result.data.length,
          mapped: batchStudents.length,
          missingUserIdButIncluded: missingUserIdCount
        });
        
        if (missingUserIdCount > 0) {
          console.warn(`⚠️ Found ${missingUserIdCount} enrollments without valid userId, used email/ID as fallback:`, batchStudents.filter(s => !s.userId));
          toast.success(`Loaded ${batchStudents.length} students. ${missingUserIdCount} students are missing userId but included using fallback info.`);
        }
      } else {
        console.error('Failed to load enrollments:', result.error);
        toast.error(result.error || 'Failed to load enrollments');
      }
    } catch (error) {
      console.error('Error loading enrollments:', error);
      toast.error('Failed to load enrollments: ' + error.message);
    }
  };

  const loadAttendance = useCallback(() => {
    if (!selectedBatch || !selectedDate) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', selectedBatch.id),
        where('date', '==', selectedDate),
        orderBy('markedAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const records = [];
        const presentSet = new Set();
        
        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          records.push(data);
          // Only add to present set if status is 'present' and date matches
          if (data.status === 'present' && data.date === selectedDate) {
            presentSet.add(data.studentId);
          }
        });
        
        setAttendanceRecords(records);
        // Update present students based on records for this specific date
        setPresentStudents(presentSet);
      }, (error) => {
        console.error('Error loading attendance (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load attendance. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query without orderBy, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', selectedBatch.id),
        where('date', '==', selectedDate)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const records = [];
        const presentSet = new Set();
        
        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          records.push(data);
          // Only add to present set if status is 'present' and date matches
          if (data.status === 'present' && data.date === selectedDate) {
            presentSet.add(data.studentId);
          }
        });
        
        // Sort client-side by markedAt descending
        records.sort((a, b) => {
          const aTime = a.markedAt?.toDate ? a.markedAt.toDate().getTime() : (a.markedAt || 0);
          const bTime = b.markedAt?.toDate ? b.markedAt.toDate().getTime() : (b.markedAt || 0);
          return bTime - aTime;
        });
        
        setAttendanceRecords(records);
        setPresentStudents(presentSet);
      }, (error) => {
        console.error('Error loading attendance (fallback query):', error);
        toast.error('Failed to load attendance. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch, selectedDate]);

  // Set up real-time listener after function is defined
  useEffect(() => {
    if (selectedBatch) {
      const unsubscribe = loadAttendance();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [selectedBatch, selectedDate, loadAttendance]);

  const checkMarkingPermission = async () => {
    if (!selectedBatch || !selectedDate) return;
    
    try {
      // Try composite query first
      let q = query(
        collection(db, 'attendanceSettings'),
        where('batchId', '==', selectedBatch.id),
        where('date', '==', selectedDate)
      );
      
      let snapshot;
      try {
        snapshot = await getDocs(q);
      } catch (queryError) {
        // If composite query fails, try fallback
        if (queryError?.code === 'failed-precondition' || 
            queryError?.message?.includes('index')) {
          // Fallback: query by batchId only, then filter client-side
          const fallbackQ = query(
            collection(db, 'attendanceSettings'),
            where('batchId', '==', selectedBatch.id)
          );
          const fallbackSnapshot = await getDocs(fallbackQ);
          snapshot = {
            docs: fallbackSnapshot.docs.filter(doc => {
              const data = doc.data();
              return data.date === selectedDate;
            })
          };
        } else {
          throw queryError;
        }
      }
      
      if (snapshot && !snapshot.empty && snapshot.docs.length > 0) {
        const setting = snapshot.docs[0].data();
        setAllowStudentMarking(setting.allowStudentMarking || false);
      } else {
        setAllowStudentMarking(false);
      }
    } catch (error) {
      console.error('Error checking marking permission:', error);
      setAllowStudentMarking(false);
    }
  };

  const toggleStudentMarking = async () => {
    if (!selectedBatch || !selectedDate) return;

    try {
      const q = query(
        collection(db, 'attendanceSettings'),
        where('batchId', '==', selectedBatch.id),
        where('date', '==', selectedDate)
      );
      const snapshot = await getDocs(q);
      
      const newValue = !allowStudentMarking;
      
      if (!snapshot.empty) {
        // Update existing setting
        const docRef = doc(db, 'attendanceSettings', snapshot.docs[0].id);
        await updateDoc(docRef, {
          allowStudentMarking: newValue,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new setting
        await addDoc(collection(db, 'attendanceSettings'), {
          batchId: selectedBatch.id,
          date: selectedDate,
          allowStudentMarking: newValue,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      setAllowStudentMarking(newValue);
      toast.success(newValue ? 'Student marking enabled for today' : 'Student marking disabled');
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const markAttendance = async (studentId, studentName, status) => {
    if (!selectedBatch || !selectedDate) return;

    try {
      // Check if already marked
      const existingQuery = query(
        collection(db, 'batchAttendance'),
        where('batchId', '==', selectedBatch.id),
        where('studentId', '==', studentId),
        where('date', '==', selectedDate)
      );
      const existing = await getDocs(existingQuery);
      
      if (!existing.empty) {
        // Update existing
        const docRef = doc(db, 'batchAttendance', existing.docs[0].id);
        await updateDoc(docRef, {
          status,
          markedAt: serverTimestamp(),
          markedBy: userProfile?.uid,
          isSelfMarked: false
        });
        toast.success(`${studentName} - Attendance updated to ${status}`);
      } else {
        // Create new
        await addDoc(collection(db, 'batchAttendance'), {
          batchId: selectedBatch.id,
          studentId,
          studentName,
          date: selectedDate,
          status,
          markedAt: serverTimestamp(),
          markedBy: userProfile?.uid,
          isSelfMarked: false
        });
        toast.success(`${studentName} - Marked as ${status}`);
      }
    } catch (error) {
      toast.error(`Failed to mark attendance for ${studentName}`);
    }
  };

  const togglePresentStudent = (studentId) => {
    const newPresent = new Set(presentStudents);
    if (newPresent.has(studentId)) {
      newPresent.delete(studentId);
    } else {
      newPresent.add(studentId);
    }
    setPresentStudents(newPresent);
  };

  const selectAll = () => {
    if (presentStudents.size === filteredEnrollments.length && filteredEnrollments.length > 0) {
      setPresentStudents(new Set());
    } else {
      const allStudentIds = filteredEnrollments
        .map(e => e.studentId || e.userId)
        .filter(Boolean);
      setPresentStudents(new Set(allStudentIds));
    }
  };

  const handleSaveAttendance = async () => {
    if (!selectedBatch || !selectedDate) {
      toast.error('Please select batch and date');
      return;
    }

    if (enrollments.length === 0) {
      toast.error('No students found in this batch');
      return;
    }

    setSavingAttendance(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      const promises = enrollments.map(async (enrollment) => {
        // CRITICAL FIX: Use studentId which is already set to enrollment.userId
        const studentId = enrollment.studentId;
        
        if (!studentId) {
          console.warn('⚠️ Skipping enrollment without studentId:', {
            enrollmentId: enrollment.id,
            email: enrollment.email
          });
          errorCount++;
          return;
        }

        const isPresent = presentStudents.has(studentId);
        const status = isPresent ? 'present' : 'absent';
        const studentName = enrollment.studentName || enrollment.userData?.fullName || enrollment.email || 'Unknown';
        
        try {
          // Check if attendance already exists
          const existingQuery = query(
            collection(db, 'batchAttendance'),
            where('batchId', '==', selectedBatch.id),
            where('studentId', '==', studentId),
            where('date', '==', selectedDate)
          );
          const existing = await getDocs(existingQuery);
          
          if (!existing.empty) {
            // Update existing
            const docRef = doc(db, 'batchAttendance', existing.docs[0].id);
            await updateDoc(docRef, {
              status,
              studentName, // Update name in case it changed
              markedAt: serverTimestamp(),
              markedBy: userProfile?.uid,
              isSelfMarked: false
            });
            console.log(`✅ Updated attendance for ${studentName} (${studentId}): ${status}`);
          } else {
            // Create new
            await addDoc(collection(db, 'batchAttendance'), {
              batchId: selectedBatch.id,
              studentId: studentId, // This matches userId from enrollment
              studentName: studentName,
              date: selectedDate,
              status,
              markedAt: serverTimestamp(),
              markedBy: userProfile?.uid,
              isSelfMarked: false
            });
            console.log(`✅ Created attendance for ${studentName} (${studentId}): ${status}`);
          }
          successCount++;
        } catch (err) {
          console.error(`❌ Error saving attendance for ${studentName}:`, err);
          errorCount++;
        }
      });

      await Promise.all(promises);
      
      if (errorCount > 0) {
        toast.success(`Attendance saved for ${successCount} student(s). ${errorCount} failed.`);
      } else {
        toast.success(`Attendance saved for ${successCount} student(s)`);
      }
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance: ' + error.message);
    } finally {
      setSavingAttendance(false);
    }
  };

  const getAttendanceForStudent = (studentId) => {
    return attendanceRecords.find(r => r.studentId === studentId);
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const studentId = enrollment.studentId || enrollment.userId;
    const studentName = enrollment.studentName || enrollment.userData?.fullName || enrollment.email || '';
    const studentEmail = enrollment.studentEmail || enrollment.userData?.email || enrollment.email || '';
    
    const matchesSearch = studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         studentEmail.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    
    if (statusFilter === 'all') return true;
    const isPresent = presentStudents.has(studentId);
    if (statusFilter === 'present') return isPresent;
    if (statusFilter === 'absent') return !isPresent;
    return false; // late filter removed for simplicity
  });

  const stats = {
    total: filteredEnrollments.length,
    present: presentStudents.size,
    absent: filteredEnrollments.length - presentStudents.size,
    late: 0, // Late status removed for simplicity, can be added back if needed
    percentage: filteredEnrollments.length > 0 
      ? ((presentStudents.size / filteredEnrollments.length) * 100).toFixed(1)
      : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Attendance Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Manage and track student attendance</p>
      </div>

      {/* Batch Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Batch
        </label>
        <select
          value={selectedBatch?.id || ''}
          onChange={(e) => {
            const batch = batches.find(b => b.id === e.target.value);
            setSelectedBatch(batch);
          }}
          className="w-full md:w-1/3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map((batch) => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
      </div>

      {selectedBatch && (
        <>
          {/* Date Selection and Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <button
                    onClick={toggleStudentMarking}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      allowStudentMarking
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {allowStudentMarking ? (
                      <ToggleRight className="w-5 h-5" />
                    ) : (
                      <ToggleLeft className="w-5 h-5" />
                    )}
                    <span>Allow Student Marking</span>
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {allowStudentMarking && (
                  <p className="text-green-600 dark:text-green-400 font-medium">
                    Students can mark their attendance for today
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Students</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg shadow p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Present</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.present}</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg shadow p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Absent</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.absent}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/20 rounded-lg shadow p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Unmarked</p>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {enrollments.length - attendanceRecords.length}
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Percentage</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.percentage}%</p>
            </div>
          </div>

          {/* Filters and Bulk Actions */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="text-gray-400" size={20} />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">All Students</option>
                  <option value="present">Present Only</option>
                  <option value="absent">Absent Only</option>
                </select>
              </div>
            </div>
            
            {/* Info */}
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>Instructions:</strong> Check the boxes next to students who are present. Unchecked students will be marked as absent. Click "Save Attendance" to save all changes.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                <strong>Present:</strong> {presentStudents.size} / {filteredEnrollments.length} students
              </p>
            </div>
          </div>

          {/* Attendance List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Student Attendance - {new Date(selectedDate).toLocaleDateString()}
                </h2>
                <div className="flex items-center space-x-3">
                  {/* Biometric Integration Controls */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={fetchBiometricAttendance}
                      disabled={isFetchingBiometric || !selectedBatch}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                      title="Fetch attendance from biometric machine"
                    >
                      {isFetchingBiometric ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Cpu className="w-4 h-4" />
                      )}
                      <span className="hidden md:inline">Biometric Sync</span>
                    </button>
                    <button
                      onClick={() => setShowBiometricModal(true)}
                      className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Biometric API Settings"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                  </div>

                  <button
                    onClick={handleSaveAttendance}
                    disabled={savingAttendance}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
                  >
                    {savingAttendance ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Save Attendance</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <button
                        onClick={selectAll}
                        className="flex items-center space-x-2 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        {presentStudents.size === filteredEnrollments.length && filteredEnrollments.length > 0 ? (
                          <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                        <span>Mark Present</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Student Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Face Capture
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredEnrollments.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        No students found
                      </td>
                    </tr>
                  ) : (
                    filteredEnrollments.map((enrollment) => {
                      const studentId = enrollment.studentId || enrollment.userId || enrollment.id;
                      const studentName = enrollment.studentName || enrollment.userData?.fullName || enrollment.email || 'Unknown';
                      const studentEmail = enrollment.studentEmail || enrollment.userData?.email || enrollment.email || '';
                      const attendance = getAttendanceForStudent(studentId);
                      const isPresent = presentStudents.has(studentId);
                      const currentStatus = isPresent ? 'present' : 'absent';
                      
                      return (
                        <tr key={studentId || enrollment.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isPresent ? 'bg-green-50 dark:bg-green-900/10' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => togglePresentStudent(studentId)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                            >
                              {isPresent ? (
                                <CheckSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                              ) : (
                                <Square className="w-6 h-6" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {studentName}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {studentEmail}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              currentStatus === 'present' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : currentStatus === 'late'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {attendance?.faceImageUrl ? (
                              <button
                                onClick={() => setShowFaceModal(attendance.faceImageUrl)}
                                className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                <Eye className="w-4 h-4" />
                                <span>View Face</span>
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Face Image Modal */}
      {showFaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Captured Face</h3>
              <button
                onClick={() => setShowFaceModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="flex justify-center">
              <img 
                src={showFaceModal} 
                alt="Captured face" 
                className="max-w-full max-h-96 rounded-lg border border-gray-300 dark:border-gray-600"
              />
            </div>
          </div>
        </div>
      )}
      {/* Biometric Settings Modal */}
      {showBiometricModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                <Cpu className="w-6 h-6 mr-2 text-purple-600" /> Biometric API Configuration
              </h3>
              <button
                onClick={() => setShowBiometricModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              saveBiometricSettings(biometricSettings);
            }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    API Endpoint URL
                  </label>
                  <input
                    type="url"
                    required
                    placeholder="https://api.biometric.com/attendance"
                    value={biometricSettings.apiUrl}
                    onChange={(e) => setBiometricSettings({...biometricSettings, apiUrl: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">The API should return attendance records for a specific date.</p>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    API Key / Token
                  </label>
                  <input
                    type="password"
                    placeholder="Enter your API key"
                    value={biometricSettings.apiKey}
                    onChange={(e) => setBiometricSettings({...biometricSettings, apiKey: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Student Mapping Field (In App)
                  </label>
                  <select
                    value={biometricSettings.mappingField}
                    onChange={(e) => setBiometricSettings({...biometricSettings, mappingField: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="email">Student Email</option>
                    <option value="userId">User ID (UID)</option>
                    <option value="biometricId">Biometric ID (Custom Field)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Student ID Field (In API)
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., student_id or email"
                    value={biometricSettings.studentIdField}
                    onChange={(e) => setBiometricSettings({...biometricSettings, studentIdField: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status Field (In API)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., status (Optional)"
                    value={biometricSettings.statusField}
                    onChange={(e) => setBiometricSettings({...biometricSettings, statusField: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    "Present" Value (In API)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., present or 1"
                    value={biometricSettings.presentValue}
                    onChange={(e) => setBiometricSettings({...biometricSettings, presentValue: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Response Data Path
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., data.records (Leave empty if response is an array)"
                    value={biometricSettings.responsePath}
                    onChange={(e) => setBiometricSettings({...biometricSettings, responsePath: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowBiometricModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-md"
                >
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;

